import { useMemo } from 'react';

import { type ViewFilters } from '@/types/schema/LiveAppView';
import { type ReportViewChart } from '@/types/schema/views/ReportView';
import { isInternalBuilderIframe } from '@/utils/iframe';
import { useChartQueryParamsContext } from './ChartQueryParamsContext';

export function useChartFilters(chart: ReportViewChart) {
  const { params, actions } = useChartQueryParamsContext();

  // The visible filters are the filters that will be visually displayed in the UI (e.g. in the filters popover or chips).
  const visibleFilters = useMemo(() => {
    // If we are inside the page editor, we want to use the preset filters from the view schema instead of the filters from the URL params
    if (isInternalBuilderIframe()) {
      return {
        match: 'and',
        rules: chart.filters.preset_filters
      } as ViewFilters;
    }

    if (params.filters && params.filters.rules.length > 0) {
      return params.filters;
    }

    return undefined;
  }, [chart.filters.preset_filters, params.filters]);

  const applyFilters = (filters: ViewFilters | undefined) => {
    actions.setChartQueryParam({ filters });
  };

  const resetFilters = () => {
    actions.setChartQueryParam({ filters: undefined });
  };

  const removeFilter = (index: number) => {
    if (!params.filters) {
      return;
    }

    if (params.filters.rules.length === 1) {
      actions.setChartQueryParam({ filters: undefined });

      return;
    }

    const updatedFiltersRules = params.filters.rules.filter((_, i) => i !== index);

    if (updatedFiltersRules.length === 0) {
      actions.setChartQueryParam({ filters: undefined });

      return;
    }

    actions.setChartQueryParam({ filters: { ...params.filters, rules: updatedFiltersRules } });
  };

  return {
    visibleFilters,
    applyFilters,
    resetFilters,
    removeFilter
  };
}
